/* ============================================
   VEJI Scroll — reveal au scroll, scroll progress, count-up
   Activé uniquement sur body.veji-page
   ============================================ */

(function () {
  'use strict';

  function init() {
    if (!document.body.classList.contains('veji-page')) {
      var retries = 0;
      var maxRetries = 30;
      var poll = function () {
        if (document.body.classList.contains('veji-page')) {
          setup();
        } else if (retries++ < maxRetries) {
          requestAnimationFrame(poll);
        }
      };
      requestAnimationFrame(poll);
      return;
    }
    setup();
  }

  function setup() {
    setupRevealOnScroll();
    setupCountUps();
    setupScrollProgressBar();
  }

  /**
   * IntersectionObserver pour révéler les .veji-reveal* à l'entrée du viewport.
   * Un seul observer global pour économiser les ressources.
   */
  function setupRevealOnScroll() {
    var revealSelectors = '.veji-reveal, .veji-reveal-left, .veji-reveal-right, .veji-reveal-scale, .veji-reveal-fade';
    var elements = document.querySelectorAll(revealSelectors);
    if (!elements.length || !('IntersectionObserver' in window)) {
      elements.forEach(function (el) { el.classList.add('is-revealed'); });
      return;
    }

    // #20 Fix "section vide au scroll rapide + reload" : le script est en
    // `defer`, il ne s'exécute donc qu'une fois tout le HTML parsé. Sur un
    // reload avec un scroll immédiat et rapide, l'utilisateur peut déjà avoir
    // dépassé une section (ex. "Les bienfaits", juste après le hero) AVANT
    // que ce script tourne et que l'observer soit attaché. Un élément déjà
    // sorti du viewport (au-dessus) n'est plus « intersecting » : l'observer
    // ne le révèle donc jamais tout de suite, seul le filet de sécurité à 2s
    // (plus bas) finit par le faire — d'où le blanc perçu. On répare ça en
    // révélant immédiatement, sans animation, tout élément déjà à portée du
    // scroll actuel (déjà visible ou déjà dépassé) au moment où l'observer
    // démarre : inutile d'animer quelque chose que l'utilisateur a déjà
    // dépassé en scrollant vite.
    var alreadyPassed = [];
    elements.forEach(function (el) {
      var rect = el.getBoundingClientRect();
      if (rect.bottom < window.innerHeight) {
        alreadyPassed.push(el);
      }
    });
    if (alreadyPassed.length) {
      alreadyPassed.forEach(function (el) {
        el.style.transition = 'none';
        el.classList.add('is-revealed');
      });
      // Laisse le style s'appliquer avant de réactiver la transition pour les
      // futures révélations (au cas où .is-revealed serait retiré ailleurs).
      requestAnimationFrame(function () {
        alreadyPassed.forEach(function (el) { el.style.transition = ''; });
      });
    }

    var observer = new IntersectionObserver(function (entries) {
      // #13 Chorégraphie : les éléments qui entrent ensemble sont révélés en
      // cascade (titre → sous-titre → CTA → visuel) plutôt qu'en bloc. On trie
      // par position verticale et on applique un délai incrémental plafonné.
      var incoming = entries.filter(function (e) { return e.isIntersecting; });
      incoming.sort(function (a, b) {
        return a.boundingClientRect.top - b.boundingClientRect.top;
      });
      incoming.forEach(function (entry, i) {
        var delay = Math.min(i, 6) * 70;
        if (delay) entry.target.style.transitionDelay = delay + 'ms';
        entry.target.classList.add('is-revealed');
        observer.unobserve(entry.target);
      });
    }, {
      // #19 threshold 0 (au lieu de 0.1) : sur un scroll rapide, un élément haut
      // (ex. liste d'accordions "bienfaits") peut traverser tout le viewport
      // sans jamais atteindre 10% de surface visible avant que l'utilisateur
      // ait déjà dépassé la section → apparition perçue en retard / vide.
      // rootMargin bas positif : on déclenche la révélation ~350px AVANT que
      // l'élément touche le bas du viewport, le temps que la transition
      // (0.9-1s) se termine avant que l'utilisateur y arrive vraiment.
      threshold: 0,
      rootMargin: '0px 0px 350px 0px'
    });

    elements.forEach(function (el) {
      if (alreadyPassed.indexOf(el) === -1) observer.observe(el);
    });

    // Fallback : si un élément n'a pas été révélé 2s après window.load
    // (observer raté pour cause de bord bas, layout shift, etc.), on force.
    function revealAll() {
      document.querySelectorAll(revealSelectors).forEach(function (el) {
        if (!el.classList.contains('is-revealed')) {
          el.classList.add('is-revealed');
        }
      });
    }
    if (document.readyState === 'complete') {
      setTimeout(revealAll, 2000);
    } else {
      window.addEventListener('load', function () {
        setTimeout(revealAll, 2000);
      });
    }
  }

  /**
   * Count-up animé sur les éléments [data-veji-count-target].
   * Démarré quand l'élément entre dans le viewport.
   */
  function setupCountUps() {
    var targets = document.querySelectorAll('[data-veji-count-target]');
    if (!targets.length || !('IntersectionObserver' in window)) {
      targets.forEach(function (el) {
        var target = parseFloat(el.getAttribute('data-veji-count-target'));
        if (!isNaN(target)) el.textContent = formatNumber(target);
      });
      return;
    }

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        var el = entry.target;
        observer.unobserve(el);
        animateCountUp(el);
      });
    }, {
      threshold: 0.4
    });

    targets.forEach(function (el) { observer.observe(el); });
  }

  function animateCountUp(el) {
    var target = parseFloat(el.getAttribute('data-veji-count-target'));
    if (isNaN(target)) return;

    var duration = 1400;
    var startTime = null;
    var startValue = 0;

    function tick(timestamp) {
      if (!startTime) startTime = timestamp;
      var elapsed = timestamp - startTime;
      var progress = Math.min(elapsed / duration, 1);
      var eased = easeOutExpo(progress);
      var value = startValue + (target - startValue) * eased;
      el.textContent = formatNumber(value);
      if (progress < 1) {
        requestAnimationFrame(tick);
      } else {
        el.textContent = formatNumber(target);
      }
    }
    requestAnimationFrame(tick);
  }

  function easeOutExpo(t) {
    return t === 1 ? 1 : 1 - Math.pow(2, -10 * t);
  }

  function formatNumber(n) {
    return Math.round(n).toString();
  }

  /**
   * Barre de progression du scroll (élément <div class="veji-scroll-progress">
   * doit être présent dans le DOM ; sinon, on l'ajoute).
   */
  function setupScrollProgressBar() {
    var bar = document.querySelector('.veji-scroll-progress');
    if (!bar) {
      bar = document.createElement('div');
      bar.className = 'veji-scroll-progress';
      bar.setAttribute('aria-hidden', 'true');
      document.body.appendChild(bar);
    }

    // Hauteur gardée en cache : la relire à chaque scroll force un layout.
    // Mise à jour au plus une fois par image.
    var scrollHeight = 0;
    var queued = false;

    function measure() {
      scrollHeight = document.documentElement.scrollHeight - window.innerHeight;
    }

    function update() {
      queued = false;
      var scrollTop = window.scrollY || document.documentElement.scrollTop;
      var progress = scrollHeight > 0 ? scrollTop / scrollHeight : 0;
      bar.style.transform = 'scaleX(' + progress.toFixed(4) + ')';
    }

    function schedule() {
      if (queued) return;
      queued = true;
      requestAnimationFrame(update);
    }

    function remeasure() {
      measure();
      schedule();
    }

    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', remeasure);
    if ('ResizeObserver' in window) {
      new ResizeObserver(remeasure).observe(document.body);
    } else {
      window.addEventListener('load', remeasure);
    }
    measure();
    update();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
